/*
  Custom sort dropdown.

  Progressive enhancement over the theme's native <select>: the select stays in
  the DOM and remains the source of truth, so Dawn's own facet handling — which
  listens for a change event on it — keeps working untouched, along with form
  submission if JavaScript never runs.

  Implements the ARIA listbox pattern: a button that opens a list of options,
  full keyboard support, and focus returned to the button on close.
*/
(function () {
  'use strict';

  var openInstance = null;

  function build(select) {
    if (select.dataset.customSelect === 'done') return;
    select.dataset.customSelect = 'done';

    var wrap = document.createElement('div');
    wrap.className = 'cselect';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'cselect__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    if (select.id) button.setAttribute('aria-labelledby', select.id + '-label ' + select.id + '-value');

    var value = document.createElement('span');
    value.className = 'cselect__value';
    if (select.id) value.id = select.id + '-value';

    var caret = document.createElement('span');
    caret.className = 'cselect__caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML =
      '<svg viewBox="0 0 12 8" fill="none"><path d="M1 1.5 6 6.5l5-5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    button.appendChild(value);
    button.appendChild(caret);

    var list = document.createElement('ul');
    list.className = 'cselect__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    if (select.id) list.setAttribute('aria-labelledby', select.id + '-label');

    Array.prototype.forEach.call(select.options, function (opt, i) {
      var li = document.createElement('li');
      li.className = 'cselect__option';
      li.setAttribute('role', 'option');
      li.setAttribute('data-value', opt.value);
      li.id = (select.id || 'cselect') + '-opt-' + i;
      li.textContent = opt.textContent.trim();
      li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
      if (opt.selected) li.classList.add('is-selected');
      list.appendChild(li);
    });

    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(button);
    wrap.appendChild(list);
    wrap.appendChild(select);
    // Kept for the change event and for no-JS submission, but out of the tab
    // order so it is not focusable twice.
    select.classList.add('cselect__native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    function sync() {
      var opt = select.options[select.selectedIndex];
      value.textContent = opt ? opt.textContent.trim() : '';
      Array.prototype.forEach.call(list.children, function (li) {
        var on = li.getAttribute('data-value') === select.value;
        li.setAttribute('aria-selected', on ? 'true' : 'false');
        li.classList.toggle('is-selected', on);
      });
      if (opt) button.setAttribute('aria-activedescendant', '');
    }

    function open() {
      if (openInstance && openInstance !== close) openInstance();
      list.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      wrap.classList.add('is-open');
      openInstance = close;
      var sel = list.querySelector('.is-selected') || list.firstElementChild;
      if (sel) focusOption(sel);
    }

    function close(refocus) {
      list.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      wrap.classList.remove('is-open');
      if (openInstance === close) openInstance = null;
      if (refocus) button.focus();
    }

    function focusOption(li) {
      Array.prototype.forEach.call(list.children, function (o) {
        o.classList.toggle('is-active', o === li);
      });
      list.setAttribute('aria-activedescendant', li.id);
      li.scrollIntoView({ block: 'nearest' });
    }

    function choose(li) {
      select.value = li.getAttribute('data-value');
      sync();
      close(true);
      // Dawn's FacetFiltersForm binds to 'input' on the form, not 'change',
      // so both are dispatched: 'input' drives the refetch, 'change' keeps any
      // other listener (analytics, apps) behaving as it would natively.
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    button.addEventListener('click', function () {
      if (list.hidden) open();
      else close(true);
    });

    list.addEventListener('click', function (e) {
      var li = e.target.closest('.cselect__option');
      if (li) choose(li);
    });

    function activeOption() {
      return list.querySelector('.is-active') || list.querySelector('.is-selected') || list.firstElementChild;
    }

    function move(dir) {
      var cur = activeOption();
      var next = dir > 0 ? cur.nextElementSibling : cur.previousElementSibling;
      if (next) focusOption(next);
    }

    wrap.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (list.hidden) open();
          else move(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (list.hidden) open();
          else move(-1);
          break;
        case 'Home':
          if (!list.hidden) { e.preventDefault(); focusOption(list.firstElementChild); }
          break;
        case 'End':
          if (!list.hidden) { e.preventDefault(); focusOption(list.lastElementChild); }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (list.hidden) open();
          else choose(activeOption());
          break;
        case 'Escape':
          if (!list.hidden) { e.preventDefault(); close(true); }
          break;
        case 'Tab':
          if (!list.hidden) close(false);
          break;
      }
    });

    // Dawn replaces the collection markup after sorting; keep the label honest.
    select.addEventListener('change', sync);

    sync();
  }

  document.addEventListener('click', function (e) {
    if (openInstance && !e.target.closest('.cselect')) openInstance(false);
  });

  function init() {
    Array.prototype.forEach.call(
      document.querySelectorAll('select.facet-filters__sort, select#SortBy-mobile'),
      build
    );
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // The facet form is re-rendered on filter changes, so rebuild afterwards.
  document.addEventListener('click', function (e) {
    if (e.target.closest('.ctax__value, .ctax__sport, .ctax-active__chip, [data-clear-all]')) {
      setTimeout(init, 400);
    }
  });
})();
